import type { Rect, Vec2 } from "../types";

export function insideRect(p: Vec2, r: Rect): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}

function insideAny(p: Vec2, corridors: Rect[]): boolean {
  for (const r of corridors) {
    if (insideRect(p, r)) return true;
  }
  return false;
}

/**
 * True when the cursor centre AND 8 points at `radius` (the 4 cardinals plus the 4
 * diagonals at radius/sqrt2 per axis) are inside the union of `corridors` — i.e. a
 * disc of that radius fits inside the maze. The diagonal probes catch concave
 * corners (e.g. an L-shaped join) that the cardinals alone step past.
 */
export function insideCorridors(p: Vec2, corridors: Rect[], radius: number): boolean {
  const diag = radius / Math.SQRT2;
  const probes: Vec2[] = [
    p,
    { x: p.x + radius, y: p.y },
    { x: p.x - radius, y: p.y },
    { x: p.x, y: p.y + radius },
    { x: p.x, y: p.y - radius },
    { x: p.x + diag, y: p.y + diag },
    { x: p.x + diag, y: p.y - diag },
    { x: p.x - diag, y: p.y + diag },
    { x: p.x - diag, y: p.y - diag },
  ];
  return probes.every((probe) => insideAny(probe, corridors));
}

/**
 * Distance from `p` to the nearest corridor wall.
 * Positive when `p` is inside a corridor (distance to that corridor's nearest edge,
 * taking the most generous containing rect). Negative when `p` is outside every
 * corridor (distance to the nearest corridor, i.e. how far outside). 0 if `corridors`
 * is empty.
 */
export function distanceToWall(p: Vec2, corridors: Rect[]): number {
  let bestInside = -Infinity;
  let isInside = false;

  for (const r of corridors) {
    if (insideRect(p, r)) {
      isInside = true;
      const d = Math.min(p.x - r.x, r.x + r.w - p.x, p.y - r.y, r.y + r.h - p.y);
      if (d > bestInside) bestInside = d;
    }
  }
  if (isInside) return bestInside;

  if (corridors.length === 0) return 0;

  let minOutside = Infinity;
  for (const r of corridors) {
    const dx = Math.max(r.x - p.x, 0, p.x - (r.x + r.w));
    const dy = Math.max(r.y - p.y, 0, p.y - (r.y + r.h));
    const d = Math.hypot(dx, dy);
    if (d < minOutside) minOutside = d;
  }
  return -minOutside;
}
